// app/javascript/src/pages/Admin/PaymentTerms/types/index.ts

// pricing_model enum mirrored from the Rails PaymentTerm model
// ({ other: 0, fixed_fee: 1, time_and_materials: 2 }). Value = enum key,
// label = titleized, ordered to match the legacy admin form.
export const PRICING_MODEL_OPTIONS: { value: string; label: string }[] = [
  { value: "other", label: "Other" },
  { value: "fixed_fee", label: "Fixed Fee" },
  { value: "time_and_materials", label: "Time And Materials" },
];

export const pricingModelLabel = (value: string | null | undefined): string =>
  PRICING_MODEL_OPTIONS.find((o) => o.value === value)?.label ?? value ?? "";

// rounding enum keys are the format strings themselves (value === label).
export const ROUNDING_OPTIONS: { value: string; label: string }[] = [
  { value: "2000.00", label: "2000.00" },
  { value: "1300.00", label: "1300.00" },
  { value: "1240.00", label: "1240.00" },
  { value: "1235.00", label: "1235.00" },
  { value: "1234.60", label: "1234.60" },
  { value: "1234.57", label: "1234.57" },
  { value: "1234.568", label: "1234.568" },
];

export type TermType = "percent" | "milestone";

export const TERM_TYPE_OPTIONS: { value: TermType; label: string }[] = [
  { value: "percent", label: "Percentage of total" },
  { value: "milestone", label: "Project milestones" },
];

// A row in the payment-terms list table.
export interface PaymentTerm {
  id: string;
  name: string;
  pricingModel: string;
  rounding: string;
  includeExpenseInRevenue: boolean;
  includeExpenseInCost: boolean;
  includeMaterials: boolean;
  default: boolean;
}

// One editable row of the embedded `terms` array.
export interface TermFormRow {
  // Stable client-side id for React keys and drag-reorder identity. NOT sent to
  // the server (terms are full-set replaced; server term ids live in `id`).
  key: string;
  id?: number; // present on read; ignored on write (terms are full-set replaced)
  description: string;
  percentageDue: number | null; // used when termType === "percent"
  phaseId: number | null; // used when termType === "milestone"
}

let termRowKeySeq = 0;

// A unique key for a new term row (used as its stable React/drag identity).
export const nextTermRowKey = (): string => `term-row-${(termRowKeySeq += 1)}`;

export const emptyTermRow = (): TermFormRow => ({
  key: nextTermRowKey(),
  description: "",
  percentageDue: null,
  phaseId: null,
});

// Controlled values for the payment-term create/edit form.
export interface PaymentTermFormValues {
  name: string;
  pricingModel: string;
  rounding: string;
  default: boolean;
  includeExpenseInRevenue: boolean;
  includeExpenseInCost: boolean;
  includeMaterials: boolean;
  travelLimit: boolean;
  termType: TermType;
  terms: TermFormRow[];
}

export const emptyPaymentTermForm: PaymentTermFormValues = {
  name: "",
  pricingModel: "fixed_fee",
  rounding: "1235.00",
  default: false,
  includeExpenseInRevenue: false,
  includeExpenseInCost: false,
  includeMaterials: false,
  travelLimit: false,
  termType: "percent",
  terms: [],
};
